import {
  ALLOCATION_ACCOUNT_TYPE_TO_DV_MAPPING,
  COMPUTED_ALLOCATION_ACCOUNT_ID,
  COMPUTED_ALLOCATION_ACCOUNT_FEES,
  COMPUTED_ALLOCATION_ACCOUNT_AMOUNT_RAW,
  COMPUTED_ALLOCATION_ACCOUNT_CODE,
  FORM_ARRAY_BENEVA_ACCOUNTS,
  FORM_ARRAY_BENEVA_GUARANTEED_FUNDS,
  DESTINATION_FIELDS,
} from '@/constants/engine';
import {ActionFactory} from '@/engine/actions';
import {recursiveReplaceIndex} from '@/engine/utils/object';
import {BatchUpdate} from '@/types/document/store';
import {
  getAllocationFormValues,
  getBenevaInvestmentAccounts,
  getBenevaGuaranteedInvestmentFunds,
  cleanupAllocationForm,
} from '../utils';

export const saveAllocationAction: ActionFactory<'saveAllocation'> = (_, store, valueResolver) => {
  return () => {
    const updates: BatchUpdate[] = [];

    const formValues = getAllocationFormValues(store, valueResolver);
    if (!formValues) return false;
    const {
      selectedAllocation,
      isPeriodicInvestment,
      isCashInvestment,
      isEFTInvestment,
      isTransferInvestment,
      accountsCount,
      depositType,
    } = formValues;
    const accountType = selectedAllocation.accountType ?? ALLOCATION_ACCOUNT_TYPE_TO_DV_MAPPING.CI;

    const isCI = accountType === ALLOCATION_ACCOUNT_TYPE_TO_DV_MAPPING.CI;

    const existingAccounts = isCI
      ? getBenevaInvestmentAccounts(valueResolver)
      : getBenevaGuaranteedInvestmentFunds(valueResolver);

    const currentAccountsCount = existingAccounts.length;

    // Track how many new accounts we've actually created (not including reused existing accounts)
    let newAccountsCreated = 0;

    for (let i = 0; i < accountsCount; i++) {
      // Get the account's computed values
      const accountId = valueResolver(
        {$ref: recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_ID, i)},
        {keepDomainReference: true},
      );
      const accountFees = valueResolver(
        {$ref: recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_FEES, i)},
        {keepDomainReference: true},
      );
      const accountAmount = valueResolver({
        $ref: recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_AMOUNT_RAW, i),
      }).replace(' %', '');
      const accountCode = valueResolver({$ref: recursiveReplaceIndex(COMPUTED_ALLOCATION_ACCOUNT_CODE, i)});

      // If the amount is 0, skip the account
      if (Number(accountAmount) <= 0) continue;

      // Check if an account with the same id already exists
      const existingAccount = existingAccounts.find(
        account => account.accountCode === accountCode && account.depositType == depositType,
      );

      // Save the account to J_benevaInvestmentAccounts
      // Use newAccountsCreated instead of i to avoid gaps in indices when reusing existing accounts
      const destinationIndex = existingAccount ? existingAccount.index : currentAccountsCount + newAccountsCreated;
      const destinationArray = isCI ? FORM_ARRAY_BENEVA_ACCOUNTS : FORM_ARRAY_BENEVA_GUARANTEED_FUNDS;

      if (!existingAccount) {
        // Increment the row count
        updates.push({type: 'increment', id: `_${destinationArray}[count]`});

        const accountIdField = recursiveReplaceIndex(DESTINATION_FIELDS[destinationArray].id, destinationIndex);
        updates.push({type: 'set', id: accountIdField, value: accountId});

        const accountFeesField = recursiveReplaceIndex(DESTINATION_FIELDS[destinationArray].fees, destinationIndex);
        updates.push({type: 'set', id: accountFeesField, value: accountFees});

        const accountCodeField = recursiveReplaceIndex(DESTINATION_FIELDS[destinationArray].code, destinationIndex);
        updates.push({type: 'set', id: accountCodeField, value: accountCode});

        const depositTypeField = recursiveReplaceIndex(
          DESTINATION_FIELDS[destinationArray].depositType,
          destinationIndex,
        );
        updates.push({type: 'set', id: depositTypeField, value: depositType});

        // Increment the counter for new accounts created
        newAccountsCreated++;
      }

      if (isPeriodicInvestment) {
        const newAmount = (Number(accountAmount) + Number(existingAccount?.amountPeriodic ?? 0)).toString();
        DESTINATION_FIELDS[destinationArray].amount.periodic.forEach(fieldId => {
          updates.push({type: 'set', id: recursiveReplaceIndex(fieldId, destinationIndex), value: newAmount});
        });
      }
      if (isCashInvestment) {
        const newAmount = (Number(accountAmount) + Number(existingAccount?.amountCash ?? 0)).toString();
        DESTINATION_FIELDS[destinationArray].amount.cash.forEach(fieldId => {
          updates.push({type: 'set', id: recursiveReplaceIndex(fieldId, destinationIndex), value: newAmount});
        });
      }
      if (isEFTInvestment) {
        const newAmount = (Number(accountAmount) + Number(existingAccount?.amountEFT ?? 0)).toString();
        DESTINATION_FIELDS[destinationArray].amount.eft.forEach(fieldId => {
          updates.push({type: 'set', id: recursiveReplaceIndex(fieldId, destinationIndex), value: newAmount});
        });
      }
      if (isTransferInvestment) {
        const newAmount = (Number(accountAmount) + Number(existingAccount?.amountTransfer ?? 0)).toString();
        DESTINATION_FIELDS[destinationArray].amount.transfer.forEach(fieldId => {
          updates.push({type: 'set', id: recursiveReplaceIndex(fieldId, destinationIndex), value: newAmount});
        });
      }
    }

    store.getState().batchUpdate(updates);

    const cleanupUpdates: BatchUpdate[] = [];

    cleanupAllocationForm(cleanupUpdates, valueResolver);

    store.getState().batchUpdate(cleanupUpdates);

    return true;
  };
};
